import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';
import type { CreateUserInputDTO } from '@src/application/users/dto/user-input.dto';

/**
 * Hook: useCreateUser
 * Creates a new user with email and roles (Admin only)
 * User will complete profile during onboarding via OTP authentication
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for creating user
 * 
 * @example
 * ```typescript
 * const { mutate: createUser, isPending, error } = useCreateUser({
 *     onSuccess: (newUser) => {
 *         console.log('User created:', newUser);
 *     }
 * });
 * 
 * // Usage
 * createUser({
 *     email: 'user@example.com',
 *     role_ids: ['role-id-1'],
 *     university_id: 'university-id' // optional
 * });
 * ```
 */
export function useCreateUser(
    options?: Omit<
        UseMutationOptions<UserDTO, Error, CreateUserInputDTO>,
        'mutationFn'
    >
) {
    const userService = getUserService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (data: CreateUserInputDTO) => {
            return await userService.create(data);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch users list
            await queryClient.invalidateQueries({ queryKey: ['users'] });

            // Optionally set the query data for this specific user
            queryClient.setQueryData(['users', data.user_id], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
